import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Trash2, Plus } from 'lucide-react';

const DenemePageManager = () => {
  const [settings, setSettings] = useState({
    title: '',
    subtitle: '',
    content_sections: [],
    cta_title: '',
    cta_description: '',
    cta_button_text: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/deneme-page`);
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      }
    } catch (error) {
      console.error('Error fetching deneme page settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/deneme-page`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(settings)
      });

      if (response.ok) {
        alert('Settings saved successfully!');
      } else {
        alert('Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Error saving settings');
    } finally {
      setSaving(false);
    }
  };

  const addSection = () => {
    setSettings({
      ...settings,
      content_sections: [
        ...settings.content_sections,
        { title: '', description: '', color: 'red' }
      ]
    });
  };

  const removeSection = (index) => {
    setSettings({
      ...settings,
      content_sections: settings.content_sections.filter((_, i) => i !== index)
    });
  };

  const updateSection = (index, field, value) => {
    const updated = [...settings.content_sections];
    updated[index][field] = value;
    setSettings({ ...settings, content_sections: updated });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-red-600 border dark:border-gray-700-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white">Demo Page Management</h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Customize the demo page content</p>
        </div>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      {/* Header Section */}
      <Card>
        <CardHeader>
          <CardTitle>Page Header</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Title</Label>
            <Input
              value={settings.title}
              onChange={(e) => setSettings({ ...settings, title: e.target.value })}
              placeholder="Demo Page"
            />
          </div>
          <div>
            <Label>Subtitle</Label>
            <Textarea
              value={settings.subtitle}
              onChange={(e) => setSettings({ ...settings, subtitle: e.target.value })}
              placeholder="This page can be customized from the admin panel"
              rows={2}
            />
          </div>
        </CardContent>
      </Card>

      {/* Content Sections */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Content Sections</CardTitle>
            <Button onClick={addSection} size="sm">
              <Plus className="w-4 h-4 mr-2" />
              Add Section
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {settings.content_sections.map((section, index) => (
            <div key={index} className="p-4 border dark:border-gray-700 rounded-lg space-y-3">
              <div className="flex items-center justify-between">
                <Label>Section {index + 1}</Label>
                <Button
                  onClick={() => removeSection(index)}
                  variant="destructive"
                  size="sm"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Title</Label>
                  <Input
                    value={section.title}
                    onChange={(e) => updateSection(index, 'title', e.target.value)}
                    placeholder="Content Title"
                  />
                </div>
                <div>
                  <Label>Color</Label>
                  <select
                    value={section.color}
                    onChange={(e) => updateSection(index, 'color', e.target.value)}
                    className="w-full px-3 py-2 border dark:border-gray-700 rounded-md"
                  >
                    <option value="red">Red</option>
                    <option value="blue">Blue</option>
                    <option value="green">Green</option>
                    <option value="purple">Purple</option>
                    <option value="yellow">Yellow</option>
                    <option value="pink">Pink</option>
                  </select>
                </div>
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={section.description}
                  onChange={(e) => updateSection(index, 'description', e.target.value)}
                  placeholder="Section description"
                  rows={2}
                />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* CTA Section */}
      <Card>
        <CardHeader>
          <CardTitle>Call-to-Action Section</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>CTA Title</Label>
            <Input
              value={settings.cta_title}
              onChange={(e) => setSettings({ ...settings, cta_title: e.target.value })}
              placeholder="Need More Information?"
            />
          </div>
          <div>
            <Label>CTA Description</Label>
            <Textarea
              value={settings.cta_description}
              onChange={(e) => setSettings({ ...settings, cta_description: e.target.value })}
              placeholder="You can customize this page from the admin panel"
              rows={2}
            />
          </div>
          <div>
            <Label>Button Text</Label>
            <Input
              value={settings.cta_button_text}
              onChange={(e) => setSettings({ ...settings, cta_button_text: e.target.value })}
              placeholder="Back to Home"
            />
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving} size="lg">
          {saving ? 'Saving...' : 'Save All Changes'}
        </Button>
      </div>
    </div>
  );
};

export default DenemePageManager;
